import apiClient from "../api/api";
import { API_ENDPOINTS } from "../api/api.config";

export type FolderConnectionSource = "API" | "DATABASE";

export interface FolderConnection {
  _id: string;
  id: string;
  document_store_id: string;
  folder_id: string;
  source_type: FolderConnectionSource;
  config_id: string;
  ai_performance_level: string;
  file_processing_type: string;
  last_synced_at?: string | null;
  last_sync_count?: number;
  last_sync_status?: string | null;
  is_active?: boolean;
  auto_sync?: boolean;
  cron_expression?: string | null;
  timezone?: string | null;
  createdAt: string;
  // enriched by the API
  config_name?: string | null;
  config_detail?: string | null;
  config_active?: boolean | null;
  config_missing?: boolean;
}

export interface ConnectPayload {
  document_store_id: string;
  folder_id?: string | null;
  source_type: FolderConnectionSource;
  config_id: string;
  ai_performance_level?: string;
  file_processing_type?: string;
}

export interface FolderConnectionLog {
  _id: string;
  connection_id: string;
  status: "success" | "failed";
  imported_count: number;
  skipped_count?: number;
  file_names?: string[];
  duration_ms?: number;
  source_type?: string;
  config_name?: string;
  message?: string;
  error_detail?: string;
  triggered_by?: string;
  createdAt: string;
}

const folderConnectionService = {
  list: async (
    document_store_id: string,
    folder_id?: string | null
  ): Promise<FolderConnection[]> => {
    const res = await apiClient.post<any>(API_ENDPOINTS.FOLDER_CONNECTION.LIST, {
      document_store_id,
      folder_id: folder_id ?? null,
    });
    const payload = res.data?.data ?? res.data;
    return payload?.connections ?? [];
  },

  logs: async (connectionId: string): Promise<FolderConnectionLog[]> => {
    const res = await apiClient.post<any>(API_ENDPOINTS.FOLDER_CONNECTION.LOGS(connectionId), {});
    const payload = res.data?.data ?? res.data;
    return payload?.logs ?? [];
  },

  connect: async (payload: ConnectPayload): Promise<FolderConnection> => {
    const res = await apiClient.post<any>(API_ENDPOINTS.FOLDER_CONNECTION.CONNECT, payload);
    return res.data?.data ?? res.data;
  },

  sync: async (
    connectionId: string,
    force = false
  ): Promise<{ success: boolean; imported: number; skipped?: number; last_synced_at: string }> => {
    const url = force
      ? `${API_ENDPOINTS.FOLDER_CONNECTION.SYNC(connectionId)}?force=true`
      : API_ENDPOINTS.FOLDER_CONNECTION.SYNC(connectionId);
    const res = await apiClient.post<any>(url, {});
    return res.data?.data ?? res.data;
  },

  remove: async (connectionId: string): Promise<{ message: string }> => {
    const res = await apiClient.delete<any>(API_ENDPOINTS.FOLDER_CONNECTION.DELETE(connectionId));
    return res.data?.data ?? res.data;
  },

  setAutoSync: async (
    connectionId: string,
    opts: { enabled: boolean; cron_expression?: string; timezone?: string }
  ): Promise<FolderConnection> => {
    const res = await apiClient.post<any>(
      API_ENDPOINTS.FOLDER_CONNECTION.AUTO_SYNC(connectionId),
      opts
    );
    return res.data?.data ?? res.data;
  },

  setActive: async (connectionId: string, active: boolean): Promise<FolderConnection> => {
    const res = await apiClient.post<any>(
      API_ENDPOINTS.FOLDER_CONNECTION.ACTIVE(connectionId),
      { active }
    );
    return res.data?.data ?? res.data;
  },
};

export default folderConnectionService;
